import { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { mvs } from 'react-native-size-matters';
import styled from 'styled-components/native';

import { CustomIcon } from './CustomIcon';
import CustomText from './CustomText';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { COLORS } from '../constants';
import { selectMainColor } from '../features/appconfig/store/selectors';
import { updateCurrentEpisode } from '../features/currentpodcast/store/actions';
import { selectCurrentEpisode } from '../features/currentpodcast/store/selectors';
import { updateRadioMode } from '../features/currentradio/store/actions';
import { selectRadioMode } from '../features/currentradio/store/selectors';
import { updateAudioIsPlaying } from '../features/web/store/actions';
import { formatPodcastEpisodeDuration } from '../helpers/durationParser';
import { getSecondaryColor } from '../helpers/getSecondaryColor';
import { useAdjustedFontSize } from '../hooks/useAdjustedFontSize';
import { useButtonColors } from '../hooks/useButtonColors';
import { useIsLoading } from '../hooks/useIsLoading';
import { useIsEpisodeRead } from '../hooks/useReadEpisodes';
import { useShadowStyles } from '../hooks/useShadowStyles';
import { PodcastEpisodeModel } from '../models';

interface Props {
  onStartAudio?: () => void;
  style?: StyleProp<ViewStyle>;
  iconSize?: number;
  size?: number;
  podcastEpisode?: PodcastEpisodeModel;
  type?: 'radio' | 'iconOnly' | 'withDuration' | 'miniPlayer';
}

export const PlayPauseButton = ({
  onStartAudio,
  style,
  iconSize = 40,
  size = 58,
  podcastEpisode,
  type = 'radio',
}: Props) => {
  const dispatch = useAppDispatch();
  const radioMode = useAppSelector(selectRadioMode);
  const currentPodcastEpisode = useAppSelector(selectCurrentEpisode);
  const isLoading = useIsLoading();
  const { buttonColors, buttonStyle, gradientBackgroundColours, iconOrTextColor } =
    useButtonColors();
  const isRead = useIsEpisodeRead(podcastEpisode?.podcastId, podcastEpisode?.id);
  const mainColor = useAppSelector(selectMainColor);
  const shadowStyles = useShadowStyles();
  const durationFontSize = useAdjustedFontSize(mvs(13));
  const buttonDynamicStyles =
    buttonStyle === 'primary'
      ? {
          borderColor: COLORS.WHITE,
          borderWidth: 5,
        }
      : {
          borderColor: buttonColors.secondaryColor,
          borderWidth: 0,
        };

  const isRadioButton = !podcastEpisode;

  const isCurrentItem =
    (radioMode && isRadioButton) ||
    (!radioMode && podcastEpisode?.id === currentPodcastEpisode.id);
  const [isStarted, setIsStarted] = useState(false);

  const onPress = async () => {
    if (isRadioButton) {
      dispatch(updateRadioMode(true));
      setIsStarted(!isStarted);
    } else {
      dispatch(updateRadioMode(false));
      dispatch(updateCurrentEpisode(podcastEpisode));
      setIsStarted(!isStarted);
      if (onStartAudio) {
        onStartAudio();
      }
    }

    dispatch(updateAudioIsPlaying(!isStarted));
  };

  const icon = useMemo(() => {
    const iconColor =
      isRead && type === 'withDuration'
        ? mainColor
        : type === 'radio'
          ? iconOrTextColor
          : COLORS.WHITE;

    return isCurrentItem && isLoading ? (
      <Loader color={iconColor} size={iconSize < 20 ? 'small' : 'large'} />
    ) : (
      <CustomIcon
        name={
          isCurrentItem && isStarted
            ? 'pause'
            : !isCurrentItem && isRead
              ? 'replay'
              : 'play_with_offset'
        }
        color={iconColor}
        size={mvs(iconSize)}
      />
    );
  }, [
    iconOrTextColor,
    iconSize,
    isCurrentItem,
    isLoading,
    isStarted,
    isRead,
    mainColor,
    type,
  ]);

  return (
    <TouchableOpacity
      hitSlop={{ top: 5, bottom: 5, left: 5, right: 5 }}
      style={[style]}
      onPress={onPress}
    >
      {type === 'iconOnly' ? (
        <View style={[styles.background, { width: mvs(size) }]}>{icon}</View>
      ) : type === 'withDuration' ? (
        <LinearGradient
          colors={
            isRead
              ? [getSecondaryColor(mainColor), getSecondaryColor(mainColor)]
              : [mainColor, mainColor]
          }
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 0.5 }}
          style={[styles.background, styles.durationBadge]}
        >
          <View style={[{ width: mvs(iconSize) }, styles.durationIcon]}>{icon}</View>

          {podcastEpisode?.duration && (
            <CustomText
              style={{
                fontSize: durationFontSize,
                color: isRead ? mainColor : COLORS.WHITE,
              }}
              weight="SemiBold"
              numberOfLines={1}
            >
              {formatPodcastEpisodeDuration(podcastEpisode?.duration)}
            </CustomText>
          )}
        </LinearGradient>
      ) : type === 'miniPlayer' ? (
        <Button style={[styles.blackButton, { width: mvs(size) }]}>{icon}</Button>
      ) : (
        // type === 'radio'
        <Button style={[buttonDynamicStyles, { width: mvs(size) }, shadowStyles]}>
          <LinearGradient
            colors={gradientBackgroundColours}
            start={{ x: 0, y: 0.5 }}
            end={{ x: 1, y: 0.5 }}
            style={[styles.background, { width: mvs(size) }]}
          >
            {icon}
          </LinearGradient>
        </Button>
      )}
    </TouchableOpacity>
  );
};

const loaderSpacing = Platform.select({
  ios: 3,
  default: 0,
});

const Loader = styled(ActivityIndicator)`
  margin-left: ${loaderSpacing}px;
  margin-top: ${loaderSpacing}px;
`;

const Button = styled.View`
  aspect-ratio: 1;
  border-radius: ${mvs(44)}px;
  border-width: 5px;
  align-self: center;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;

const styles = StyleSheet.create({
  background: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  durationBadge: {
    borderRadius: mvs(30),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: mvs(2),
    paddingHorizontal: mvs(7),
    gap: mvs(4),
    minWidth: mvs(30),
  },
  durationIcon: {
    alignItems: 'center',
  },
  blackButton: {
    backgroundColor: '#252525',
    borderWidth: 0,
  },
});
